/* eslint-disable react/prop-types */
import React, { Component } from 'react';

import cn from 'classnames';
import { noop } from 'lodash';
import { node, bool, string, func, shape } from 'prop-types';
import { withState } from 'recompose';

import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popper';
import withStyles from '@mui/styles/withStyles';

import ClickableComponent from 'components/Common/ClickableComponent';
import Tooltip from 'components/Common/Tooltip';
import styles from 'components/Common/WithPopover/styles';
import withMediaInfo from 'HOC/withMediaInfo';

import { ensureArray } from 'utils/arrays';

@withStyles(styles)
@withState('anchor', 'setAnchor', null)
@withMediaInfo()
class WithPopover extends Component {
  constructor(props) {
    super(props);
    this.state = {
      arrowRef: null,
    };
  }

  componentDidUpdate(prevProps) {
    // Whenever manualClose has changed state to true, this will force close the popover
    // onCloseCallback must be a provided function in the parent component that will reset the state
    const { manualClose, onCloseCallback } = this.props;
    if (
      manualClose &&
      typeof onCloseCallback === 'function' &&
      manualClose !== prevProps.manualClose
    ) {
      onCloseCallback();
      this.forceClose();
    }
  }

  setArrowRef = arrowRef => this.setState({ arrowRef });

  toggleAnchor = e => {
    const { anchor, setAnchor, closeOnClickAwayOnly, onClick } = this.props;
    if (anchor && !closeOnClickAwayOnly) {
      setAnchor(null);
    } else {
      setAnchor(e.currentTarget);
    }
    if (onClick) {
      onClick(e);
    }
  };

  forceClose = () => {
    const { setAnchor } = this.props;
    setAnchor(null);
  };

  render() {
    const {
      paperClass,
      mediaInfo,
      classes,
      anchor,
      setAnchor,
      forceRemainOpen,
      popover = () => null,
      render = () => null,
      disabled = false,
      disabledMessage = 'Disabled',
      messagePlacement = 'top',
      placement = 'bottom',
      clickable = mediaInfo.isMobile,
      buttonStyle,
      buttonClassName,
      disablePortal,
      popoverStyle,
      paperStyle,
      resetForceOpen,
      hideArrow,
      roundArrow,
      popperModifiers = [],
      arrowClass,
      useClickableComponent,
      onCloseCallback,
      popperOptions,
      'aria-label': ariaLabel,
    } = this.props;

    const isOpen = forceRemainOpen || Boolean(anchor);

    const { arrowRef } = this.state;
    const renderFunctionProps = {
      setAnchor,
      anchor,
      isOpen,
      toggleAnchor: this.toggleAnchor,
    };

    const defaultPopperModifiers = [
      {
        name: 'arrow',
        options: {
          enabled: Boolean(arrowRef),
          element: arrowRef,
        },
      },
      {
        name: 'preventOverflow',
        options: {
          boundariesElement: 'viewport',
        },
      },
    ];

    const popoverContent = popover({ setAnchor, forceClose: this.forceClose });

    const content = (
      <>
        {disabled ? (
          <Tooltip placement={messagePlacement} title={disabledMessage}>
            {render(renderFunctionProps)}
          </Tooltip>
        ) : (
          render(renderFunctionProps)
        )}
        {!disabled && popoverContent && (
          <Popover
            disablePortal={disablePortal}
            className={classes.arrowPopper}
            open={isOpen}
            anchorEl={anchor}
            anchorReference="anchorEl"
            onClose={() => setAnchor(null)}
            placement={placement}
            popperOptions={{
              modifiers: [
                ...defaultPopperModifiers,
                ...ensureArray(popperModifiers),
              ],
              ...popperOptions,
            }}
            style={{
              zIndex: forceRemainOpen ? -1 : 9999,
              ...popoverStyle,
            }}
          >
            <ClickAwayListener
              mouseEvent="onMouseDown"
              touchEvent="onTouchStart"
              onClickAway={e => {
                e.preventDefault();
                e.stopPropagation();
                // When the popover is open and the user clicks the space key,
                // this onClickAway is triggered, which will close the popover.
                // (Somehow clicking space triggers a "click" event in the <ClickableComponent />)
                // This is not ideal, especially if the popover contains a text input.
                // So the solution is to prevent removing the anchor when
                // the target and the anchor both point to the <ClickableComponent />.
                if (e.target !== anchor) {
                  setAnchor(null);
                }
                if (typeof onCloseCallback === 'function') {
                  onCloseCallback();
                }
              }}
            >
              <div className={classes.paperContainer}>
                {!hideArrow && (
                  <span
                    className={cn(arrowClass, {
                      [classes.arrowArrow]: !roundArrow,
                      [classes.roundArrow]: roundArrow,
                    })}
                    ref={this.setArrowRef}
                  />
                )}
                <Paper
                  className={cn(classes.paper, paperClass)}
                  style={paperStyle}
                >
                  {popover({ setAnchor, forceClose: this.forceClose })}
                </Paper>
              </div>
            </ClickAwayListener>
          </Popover>
        )}
      </>
    );

    return useClickableComponent ? (
      <ClickableComponent
        aria-label={ariaLabel}
        aria-haspopup
        style={buttonStyle}
        className={buttonClassName}
        onMouseEnter={e => {
          if (!clickable) {
            setAnchor(e.currentTarget);
            const documentNamingModalIsOpen =
              document.getElementById('document-upload');
            if (
              forceRemainOpen &&
              !documentNamingModalIsOpen &&
              resetForceOpen
            ) {
              resetForceOpen();
            }
          }
        }}
        onMouseLeave={() => {
          if (!clickable) {
            setAnchor(null);
          }
        }}
        onClick={e => {
          e.stopPropagation();
          if (clickable && !disabled) {
            this.toggleAnchor(e);
          }
        }}
      >
        {content}
      </ClickableComponent>
    ) : (
      content
    );
  }
}

WithPopover.propTypes = {
  anchor: node,
  /** If it's set to true, the content will be wrapped inside the ClickableComponent.
   * If it's set to false, we need to use toggleAnchor() function to toggle the popover */
  useClickableComponent: bool,
  paperClass: string,
  setAnchor: func,
  closeOnClickAwayOnly: bool,
  mediaInfo: shape({
    isMobile: bool,
    isTablet: bool,
    isDesktop: bool,
  }),
  onClick: func,
  onCloseCallback: func,
  /*
   * Used to manually close the popover when triggered by user i.e button click
   * Set state in parent component to keep track of open/close state
   * Reset state using onCloseCallback (This function is required if using this parameter)
   */
  manualClose: bool,
  classes: shape({}),
  /** Additional options passed to the popper component: https://popper.js.org/docs/v2/constructors/#options */
  popperOptions: shape({}),
  'aria-label': string,
};

WithPopover.defaultProps = {
  anchor: null,
  paperClass: undefined,
  useClickableComponent: true,
  closeOnClickAwayOnly: false,
  onClick: null,
  onCloseCallback: null,
  manualClose: false,
  classes: {},
  mediaInfo: {},
  setAnchor: noop,
  popperOptions: {},
  'aria-label': undefined,
};

export default WithPopover;
